import { Button, IconButton, InputAdornment, Typography } from "@mui/material";
import { useState } from "react";
import { _post } from "../services/api.service";
import "../styles/login.css";
import { Link, useNavigate } from "react-router-dom";
import { Validation } from "../validation/validation";
import { COLORS } from "../theme/theme.config";
import logo from "../images/logo-with-caption.webp";
import StyledTextField from "../components/styled-text-field.compoment";
import GoogleLoginButton from "../components/login-with-google.component";

import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

export interface Field {
  value?: string | boolean;
  error?: string;
}

export interface Form {
  email: Field;
  password: Field;
  confirmPassword: Field;
}

export interface FormValidation {
  email: Validation.Validator[];
  password: Validation.Validator[];
}

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState<Form>({} as any);
  const navigate = useNavigate();
  localStorage.removeItem("IMAGE_TO_BE_DELTED");

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const updateForm = (
    key: string,
    validators: Validation.Validator[],
    value: string | boolean
  ) => {
    const validationData = Validation.validate(value, validators);
    const errors: Validation.Validator[] = validationData.errors;
    setForm({
      ...form,
      [key]: {
        value,
        error: errors.length > 0 ? errors[0].message : "",
      },
    });
  };

  const formValidation: FormValidation = {
    email: [
      {
        validatorType: Validation.ValidationType.REQUIRED,
        message: "This field is required",
      },
      {
        validatorType: Validation.ValidationType.EMAIL,
        message: "Invalid email format",
      },
    ],
    password: [
      {
        validatorType: Validation.ValidationType.REQUIRED,
        message: "This field is required",
      },
      {
        validatorType: Validation.ValidationType.MIN,
        message: "Your password must be longer than 8 characters",
        props: {
          min: 8,
        },
      },
    ],
  };

  const onEmailChange = (e: any) => {
    setEmail(e.target.value);
    updateForm("email", formValidation.email, e.target.value);
  };

  const onPasswordChange = (e: any) => {
    setPassword(e.target.value);
    updateForm("password", formValidation.password, e.target.value);
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    sessionStorage.setItem("USER_EMAIL", email);
    const payload = { email, password };
    const userData = await _post("users/api/token/", payload);
    if (userData) {
      localStorage.setItem("TOKEN", userData.access);
      localStorage.setItem("REFRESH_TOKEN", userData.refresh);
      navigate("/");
    }
  };

  return (
    <div className="outer">
      <div className="middle">
        <div className="login_form">
          <div className="mb-4">
            <img src={logo} alt="Logo" style={{ height: "40px" }} />
          </div>
          <div className="content-container">
            <h1 className="login-heading">Welcome Back</h1>
            <div className="login-description">
              <p>
                Having trouble logging in? Email support@theunplan.com.
              </p>
            </div>
          </div>
          <div style={{ margin: "10px 0 20px", textAlign: "center" }}>
            <GoogleLoginButton></GoogleLoginButton>
          </div>
          <div
            style={{
              textAlign: "center",
              margin: "0 0 10px",
              color: "#808080",
              fontSize: "12px",
            }}
          >
            OR
          </div>
          <form onSubmit={handleLogin} autoComplete="off">
            <div className="mb-3">
              <StyledTextField
                fullWidth
                label="Email"
                type="email"
                variant="outlined"
                inputMode="email"
                autoFocus
                required
                autoCapitalize="none"
                onChange={onEmailChange}
                inputProps={{ spellCheck: "false" }}
              />
              <div className="error-container d-none">
                <p className="error">{form.email?.error}</p>
              </div>
            </div>
            <div className="mb-2">
              <StyledTextField
                fullWidth
                type={showPassword ? "text" : "password"}
                label="Password"
                variant="outlined"
                required
                color="secondary"
                onChange={onPasswordChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        sx={{
                          color: "#808080",
                        }}
                        onClick={togglePasswordVisibility}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <RemoveRedEyeOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <div className="error-container d-none">
                <p className="error">{form.password?.error}</p>
              </div>
            </div>
            <div
              className="mb-4"
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Link
                to="/password-reset"
                style={{
                  color: COLORS.green,
                  textDecoration: "none",
                  fontWeight: "bold",
                  fontSize: "13px",
                }}
              >
                Forgot Password?
              </Link>
              <Link
                style={{
                  color: COLORS.green,
                  textDecoration: "none",
                  fontWeight: "bold",
                  fontSize: "13px",
                }}
                to="/verify-code"
              >
                Activate Account
              </Link>
            </div>
            <Button
              id="form-login"
              sx={{
                backgroundColor: COLORS.green,
                padding: "14px",
                borderRadius: "0",
                fontSize: "1rem",
                color: "#09091c",
                "&:hover": { backgroundColor: COLORS.greenHover },
                "&:disabled": {
                  backgroundColor: COLORS.greenDisabled,
                  color: "#09091c",
                },
              }}
              variant="contained"
              fullWidth
              type="submit"
            >
              Login
            </Button>
            <p className="sign_up mt-4">
              Don't have an account?
              <Link
                style={{
                  marginLeft: "10px",
                  color: COLORS.green,
                  textDecoration: "none",
                  fontWeight: "bold",
                  fontSize: "13px",
                }}
                to="/create-account"
              >
                Sign up
              </Link>
            </p>
          </form>
        </div>
        <Typography
          variant="body2"
          sx={{
            position: "absolute",
            bottom: 10,
            width: "100%",
            textAlign: "center",
            fontSize: 13,
            color: "#9da7b2",
            px: 2,
          }}
        >
          By using theunplan.com, you agree to our{" "}
          <a
            href="https://theunplan.com/tnc"
            target="_blank"
            rel="noreferrer"
            style={{
              color: "#626876",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            terms of service
          </a>
          .
        </Typography>
      </div>
    </div>
  );
};

export default Login;
